import { isPlatformBrowser } from '@angular/common';
import { combineLatest, fromEvent, Subscription } from 'rxjs';

import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, HostListener,
  Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';

import { PushSizeEnum } from './push-card.enum';
import { SdRichParagraphComponent } from '../rich-paragraph/rich-paragraph.component';
import { BrandThemesEnum, ThemesEnum } from '../../_enums/themes.enum';
import { DeviceService } from '../../_services/device.service';
import { IExtendedLink, ILinkMedia } from '../../_interfaces/link.interface';
import { ImageRatioEnum } from '../../_enums/image-ratio.enum';
import { IPushCard, IPushCardWithLayout } from '../../_interfaces/push-card.interface';
import { PositionEnum } from '../../_enums/position.enum';
import { SdBannerComponent } from '../medias/banner/banner.component';
import { SdLibConfig } from '../../sd-lib-config';
import { SizesEnum } from '../../_enums/sizes.enum';
import { BrandsEnum } from '../../_enums/brands.enum';
import { SdBrandBarComponent } from '../brand-bar/brand-bar.component';
import { SdThemeComponent } from '../abstract/theme/theme.component';
import { AnimationsEnum } from '../../_enums/animations.enum';

@Component({
  selector: 'sd-push-card',
  templateUrl: './push-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    SdRichParagraphComponent,
    SdBannerComponent,
    SdBrandBarComponent
  ]
})
export class SdPushCardComponent extends SdThemeComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  @Input()
  indexFollow = true;

  @Input()
  isLoading = false;

  @Input()
  push: IPushCard;

  @Input()
  hideButtonLink = false;

  /**
   * Define the ratio of the image 16/9, 1/1, 4/3 ...
   * * @param {string}
   */
  @Input()
  imageRatio: ImageRatioEnum;

  /**
   * Allow to define the level of the title tag (h1 - h6).
   * * @param {number} 1 to 6
   */
  @Input()
  titleLevel = 3;

  @Input()
  subtitleLevel?: number;

  /**
   * Determines whether the title is inside a hx tag or in a p tag for the title
   */
  @Input()
  isDescription = false;

  @Input()
  hasModal = true;

  /**
   * Allow to define if the text is aligned to the left or the right, or even centered
   * * @param {string} left | right | center
   */
  @Input()
  textConfig: PositionEnum.left | PositionEnum.center = PositionEnum.center;

  /**
   * Allow to define if the image is on the left or on the right of the main content
   * * @param {string} left | right | top | bottom
   */
  @Input()
  mediaConfig: PositionEnum;

  /**
   * Allow to define if the image is set as background for the whole section
   */
  @Input()
  mediaAsBackground = true;

  /**
   * Allow to define either the theme is dark or light (default)
   * * @param {string} dark | light | brand | and marketing
   */
  @Input()
  themesConfig: ThemesEnum | BrandThemesEnum;

  /**
   * Allow to define de size of the push wanted
   * Changes content size
   */
  @Input()
  pushSize: PushSizeEnum | SizesEnum;

  @Input()
  isFit: boolean;

  @Input()
  hasReadMore = false;

  @Input()
  twicPicsAutoSize = true;

  @Input()
  brandBar: BrandsEnum;

  @ViewChild('content')
  content: SdRichParagraphComponent;

  @ViewChild('media')
  media: SdBannerComponent;

  @Output()
  clicked = new EventEmitter<IExtendedLink | ILinkMedia>();

  @Output()
  videoPlayed = new EventEmitter<{ status: string; title: string }>();

  @Output()
  videoEnded = new EventEmitter<{ status: string; title: string }>();

  @Output()
  cardClicked = new EventEmitter<void>();

  @Output()
  clickCardFrames = new EventEmitter<IPushCard | IPushCardWithLayout>();

  @Output()
  carouselClicked = new EventEmitter<void>();

  @Input()
  class = ''; // override the standard class attr with a new one.

  @HostBinding('class')
  get hostClasses(): string {
    return this.getClasses();
  }

  @HostListener('click')
  onCardClick() {
    this.cardClicked.emit();
  }

  getClasses() {
    return [
      'c__push-card',
      this.class,
      this.mediaConfig ? 'is-' + this.mediaConfig : '',
      this.themesConfig ? 'is-theme-' + this.themesConfig : '',
      this.pushSize ? 'is-size-' + this.pushSize : '',
      this.animation ? 'is-animation-' + this.animation : '',
      this.canApplyRatio ? 'has-ratio' : '',
      this.mediaAsBackground ? 'has-background' : '',
      this.showPushBorder ? 'has-border' : ''
    ]
      .filter(Boolean)
      .join(' ');
  }

  animation: AnimationsEnum;
  showPushBorder: boolean;
  isDesktopLarge$: boolean;
  isTablet$: boolean;
  isMobile$: boolean;
  subscriptions = new Subscription();
  themesEnum = ThemesEnum;
  isOpen = false;
  limit = 150;
  canApplyRatio = true;
  hasNoData = false;
  hasLinks = false;
  currRatio: { x: number; y: number };
  contentSize: SizesEnum.xl | SizesEnum.l | SizesEnum.m | SizesEnum.s;
  positionEnum = PositionEnum;
  _titleLevel: number;

  constructor(
    private deviceService: DeviceService,
    private sdLibConfig: SdLibConfig,
    private cdRef: ChangeDetectorRef
  ) {
    super();

    const { showPushBorder, animation } = this.sdLibConfig.get();

    this.showPushBorder = showPushBorder;
    this.animation = animation;
  }

  ngOnInit() {
    this.subscriptions.add(
      combineLatest([
        this.deviceService.isDesktopLarge,
        this.deviceService.isTablet,
        this.deviceService.isMobile
      ]).subscribe(([isDesktopLarge, isTablet, isMobile]) => {
        this.isDesktopLarge$ = isDesktopLarge;
        this.isTablet$ = isTablet;
        this.isMobile$ = isMobile;
        this.cdRef.detectChanges();
        this.manageSize();
      })
    );

    if (this.push) {
      this.hasLinks = Object.entries(this.push)
        .filter((data) => data[0] !== 'links')
        .every((x) => x[1] === null || x[1] === '');
    }

    if (isPlatformBrowser(this.platformId)) {
      this.subscriptions.add(
        fromEvent(window, 'resize').subscribe(() => {
          setTimeout(() => {
            this.getRatio();
            this.manageSize();
            this.cdRef.markForCheck();
          }, 500);
        })
      );
    }

    this.manageSize();
    this.manageImageRatio();
    this._titleLevel = this.titleLevel;
  }

  ngAfterViewInit() {
    if (isPlatformBrowser(this.platformId) && !this.isLoading) {
      setTimeout(() => {
        this.getRatio();
        this.cdRef.markForCheck();
      }, 500);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    super.ngOnChanges(changes);

    const { animation, showPushBorder } = this.sdLibConfig.get(this.brand);

    this.animation = animation;
    this.showPushBorder = showPushBorder;
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  checkData(event: boolean) {
    this.hasNoData = event;
  }

  manageSize() {
    switch (this.pushSize) {
      case PushSizeEnum.l:
        this.contentSize = this.isMobile$
          ? SizesEnum.s
          : this.isTablet$
            ? SizesEnum.m
            : this.isDesktopLarge$
              ? SizesEnum.xl
              : SizesEnum.l;
        break;
      case PushSizeEnum.m:
        this.contentSize = this.isMobile$
          ? SizesEnum.s
          : this.isTablet$
            ? SizesEnum.m
            : this.isDesktopLarge$
              ? SizesEnum.l
              : SizesEnum.m;
        break;
      case PushSizeEnum.s:
        this.contentSize = this.isMobile$
          ? SizesEnum.s
          : this.isTablet$
            ? SizesEnum.s
            : SizesEnum.m;
        break;
      case PushSizeEnum.xs:
        this.contentSize = this.isMobile$
          ? SizesEnum.s
          : this.isTablet$
            ? SizesEnum.s
            : SizesEnum.m;
        break;
      case PushSizeEnum.xxs:
        this.contentSize = SizesEnum.s;
        break;
    }
  }

  manageImageRatio() {
    if (this.imageRatio) {
      switch (this.imageRatio) {
        case ImageRatioEnum.r16_9:
          this.currRatio = { x: 16, y: 9 };
          break;
        case ImageRatioEnum.r4_3:
          this.currRatio = { x: 4, y: 3 };
          break;
        case ImageRatioEnum.r1_1:
          this.currRatio = { x: 1, y: 1 };
          break;
      }
    }
  }

  getRatio() {
    if (this.imageRatio && this.currRatio) {
      const mediaWidth = this.elRef.nativeElement.offsetWidth;
      const mediaHeight = (mediaWidth * this.currRatio.y) / this.currRatio.x;
      const contentHeight = this.content?.content?.nativeElement.offsetHeight;
      const ratio = mediaHeight / contentHeight;
      this.canApplyRatio = ratio >= 1;
    }
  }

  onVideoPlayed() {
    this.videoPlayed.emit({
      status: 'play',
      title: this.push?.richParagraph?.heading?.data?.title
    });
  }

  onVideoEnded() {
    this.videoEnded.emit({
      status: 'ended',
      title: this.push?.richParagraph?.heading?.data?.title
    });
  }

  onClick(event: { link: IExtendedLink | ILinkMedia }) {
    this.clicked.emit(event.link);
    this.carouselClicked.emit();
  }

  onClickCardFrame(push: IPushCard | IPushCardWithLayout) {
    this.clickCardFrames.emit(push);
  }
}
